import { useOptionalAuth } from './useOptionalAuth';

interface FieldRoles {
  isReader: boolean;
  isAdmin: boolean;
  hasAny: boolean;
}

// Decode Keycloak realm_access.roles + resource_access[*].roles from the
// access token and check for field:reader / field:admin. field:admin is a
// composite that includes field:reader, so an admin satisfies both.
//
// Mirrors the JWT decode pattern used by hasAdminRole in components/AppShell.tsx.
function rolesFromToken(token?: string | null): Set<string> {
  if (!token) return new Set();
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as {
      realm_access?: { roles?: string[] };
      resource_access?: Record<string, { roles?: string[] }>;
    };
    const roles = new Set<string>();
    for (const r of payload.realm_access?.roles || []) roles.add(r);
    for (const client of Object.values(payload.resource_access || {})) {
      for (const r of client.roles || []) roles.add(r);
    }
    return roles;
  } catch {
    return new Set();
  }
}

export function useFieldRole(): FieldRoles {
  const auth = useOptionalAuth();
  const roles = rolesFromToken(auth?.user?.access_token);
  const isAdmin = roles.has('field:admin');
  const isReader = isAdmin || roles.has('field:reader');
  return { isReader, isAdmin, hasAny: isReader || isAdmin };
}
